import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SerializeFrom } from '@remix-run/node';
import { useFetcher } from '@remix-run/react';
import { atom, useAtom } from 'jotai';
import {
    CONVERSATIONS_API,
    type loader,
} from '~/routes/remix-api.chat.conversations';
import type { Conversation } from '../types';
import { useConversationCache } from './use-conversation-cache';

type ConversationsResult = NonNullable<SerializeFrom<typeof loader>>;
type PageInfo = ConversationsResult['pageInfo'];

const conversationsAtom = atom<Conversation[] | null>(null);
const pageInfoAtom = atom<PageInfo | null>(null);

/**
 * Hook to get a paginated list of conversations for the current user.
 * @param limit The maximum number of conversations to return. Do not change this
 *  value. Defaults to 20.
 */
export function useConversations({
    limit = 20,
}: {
    limit?: number;
} = {}) {
    const { lastCachedConversation } = useConversationCache();
    const isFetched = useRef(false);
    const [search, setSearch] = useState('');
    const [buildHubIDs, setBuildHubIDs] = useState<string[]>([]);
    const [isArchived, setIsArchived] = useState(false);
    const [offset, setOffset] = useState(0);
    const [conversations, setConversations] = useAtom(conversationsAtom);
    const [pageInfo, setPageInfo] = useAtom(pageInfoAtom);
    const fetcher = useFetcher<typeof loader>();
    const apiUrl = useMemo(() => {
        const url = new URL(CONVERSATIONS_API, window.location.origin);
        url.searchParams.set('q', search);
        url.searchParams.set('isArchived', isArchived ? '1' : '0');
        url.searchParams.set('limit', String(limit));
        url.searchParams.set('offset', String(offset));
        for (const buildHubID of buildHubIDs) {
            url.searchParams.append('buildHubIDs', buildHubID);
        }
        return `${url.pathname}${url.search}`;
    }, [search, buildHubIDs, isArchived, limit, offset]);

    const setParams = useCallback(
        (params: {
            search?: string;
            buildHubIDs?: string[];
            isArchived?: boolean;
        }) => {
            if (params.search !== undefined) {
                setSearch(params.search.trim());
            }
            if (params.buildHubIDs !== undefined) {
                setBuildHubIDs(params.buildHubIDs);
            }
            if (params.isArchived !== undefined) {
                setIsArchived(params.isArchived);
            }
            setOffset(0);
        },
        []
    );

    const resetParams = useCallback(() => {
        setParams({ search: '', buildHubIDs: [], isArchived: false });
    }, [setParams]);

    const loadMore = useCallback(() => {
        if (
            fetcher.state !== 'idle' ||
            !conversations ||
            !pageInfo?.hasNextPage
        ) {
            return;
        }
        setOffset(conversations.length);
    }, [fetcher.state, conversations, pageInfo]);

    useEffect(() => {
        // Fetch initial conversations on mount.
        if (fetcher.state === 'idle' && !isFetched.current) {
            fetcher.load(apiUrl);
            isFetched.current = true;
        } else if (isFetched.current) {
            // Refetch when the query params change.
            fetcher.load(apiUrl);
        }
        // Must not include `fetcher` to avoid infinite loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl]);

    // Set data from the fetcher.
    useEffect(() => {
        if (!fetcher.data || fetcher.state !== 'idle') {
            return;
        }
        const newConversations = fetcher.data.conversations;
        const newPageInfo = fetcher.data.pageInfo;
        setConversations((prev) => {
            if (!prev || !newPageInfo.hasPreviousPage) {
                return newConversations;
            }
            const next = [...prev];
            for (const conversation of newConversations) {
                if (!prev.some((c) => c.ID === conversation.ID)) {
                    next.push(conversation);
                }
            }
            return next;
        });
        setPageInfo(newPageInfo);
    }, [fetcher, setConversations, setPageInfo]);

    // Update the chat settings and unread message count as soon as possible
    // from the conversation cache.
    useEffect(() => {
        if (!lastCachedConversation) return;
        setConversations((prev) => {
            if (prev?.some((c) => c.ID === String(lastCachedConversation.ID))) {
                return prev.map((c) =>
                    c.ID === String(lastCachedConversation.ID)
                        ? {
                              ...c,
                              UnreadMessageCount:
                                  lastCachedConversation.UnreadMessageCount,
                              Unread: lastCachedConversation.Unread,
                              IsArchived: lastCachedConversation.IsArchived,
                              IsMuted: lastCachedConversation.IsMuted,
                              ...(lastCachedConversation.__typename ===
                              'DirectConversation'
                                  ? {
                                        Name: lastCachedConversation.Name,
                                        BuildHub:
                                            lastCachedConversation.BuildHub,
                                        Thumbnail:
                                            lastCachedConversation.Thumbnail,
                                        IsArchived:
                                            lastCachedConversation.IsArchived,
                                    }
                                  : {}),
                          }
                        : c
                );
            }
            return prev;
        });
    }, [lastCachedConversation, setConversations]);

    return {
        /**
         * The list of conversations.
         */
        conversations,
        /**
         * There are more conversations to load.
         */
        hasMore: pageInfo?.hasNextPage ?? false,
        /**
         * The conversations are currently being loaded.
         */
        isLoading: fetcher.state !== 'idle',
        /**
         * Set the filter parameters for the `GetConversations` query. Parameters
         * that are `undefined` will not be changed.
         */
        setParams,
        /**
         * Reset the filter parameters to the default values.
         */
        resetParams,
        /**
         * Refetch the conversations.
         */
        refetch: useCallback(() => {
            setOffset(0);
            fetcher.load(apiUrl);
        }, [fetcher, apiUrl]),
        /**
         * Load more conversations by fetching the next page. This increments the
         * offset and appends the new results to the existing conversations.
         */
        loadMore,
    };
}

/**
 * Hook to manage the conversations from the useConversations hook. Use this
 * hook to manipulate the conversations data with fetching from the server.
 */
export function useConversationsState() {
    const [conversations, setConversations] = useAtom(conversationsAtom);
    const [pageInfo] = useAtom(pageInfoAtom);

    const removeConversation = useCallback(
        (conversationID: number) => {
            setConversations((prev) => {
                if (!prev) return null;
                return prev.filter((c) => c.ID !== String(conversationID));
            });
        },
        [setConversations]
    );

    return {
        conversations,
        pageInfo,
        /**
         * Remove a conversation from the list of conversations.
         */
        removeConversation,
    };
}
